import { useMemo, useState, type ComponentProps } from "react"
import { format } from "date-fns"
import { formatNumber } from "@/v2-common/utils/utils.formatting"
import cn from "@/v2-ui/utils/utils.cn"
import Chart from "@/v2-ui/chart"
import { DATA_VIZ } from "@/v2-ui/color/dataViz.colors"
import {
  formatAppArea,
  formatAppCode,
  getAppCodeColor,
} from "@/v2-backoffice-client/usage/usage.constants"
import {
  deriveDailyTotals,
  padDailyTotals,
  getAreaDayApps,
  type UsageAreaDayRow,
} from "@/v2-backoffice-client/usage/usage.utils"
import { getHolidaySpans } from "@/v2-backoffice-client/usage/usage.seasons"

const RANGE_OPTIONS = [
  { label: "4 weeks", value: 28 },
  { label: "13 weeks", value: 90 },
]

const TOOLTIP_AREA_LIMIT = 3

/**
 * Trailing window for the trendline — 7 days flattens the weekday/weekend
 * sawtooth that makes raw daily bars hard to read a direction from.
 * Exported for consumers that render their own legend (Portfolio hero).
 */
export const TREND_WINDOW_DAYS = 7

// Brand data green, not the theme's `--primary` (amber chrome in the
// backoffice theme): data series read apart from buttons and chips.
const CHART_CONFIG = {
  value: {
    label: "Weighted activity",
    color: DATA_VIZ.brand.green2,
  },
}

export type UsageActivityChartProps = ComponentProps<"div"> & {
  /** Raw per-day, per-area rows from the usage queries (90-day window). */
  areaDays: UsageAreaDayRow[]
  /**
   * Fixed window length in days. When set, the built-in 4/13-week range
   * switcher is hidden and the chart pads exactly this many days — the
   * Portfolio's global timeframe drives it instead.
   */
  windowDays?: number
  /** Last day of the fixed window (defaults to today); pairs with windowDays. */
  windowEnd?: Date
  /**
   * Hide the built-in title/caption/legend row — for hosts that render
   * their own section header (the Portfolio hero) and would duplicate it.
   */
  hideHeader?: boolean
}

type ChartPoint = {
  day: string
  value: number
  rawCount: number
  /** Trailing 7-day mean of the weighted total — the trendline. */
  trend: number
  /** Weighted value per app code for the stacked bars ("app:v1-console"). */
  [appValue: `app:${string}`]:
    | number
    | string
    | { area: string; value: number }[]
    | undefined
  topAreas: { area: string; value: number }[]
}

// Daily weighted-activity bar chart with a real hover tooltip (date, the
// weighted + raw numbers, and what the activity was about that day),
// modeled on the uptime response chart: responsive container, range
// switcher, quiet days rendered as gaps instead of being skipped. When more
// than one product contributed (Console v1 + v2), the bars stack per app so
// the product split is visible day by day.
function UsageActivityChart(props: UsageActivityChartProps) {
  const {
    areaDays,
    windowDays,
    windowEnd,
    hideHeader,
    className,
    ...divProps
  } = props

  const [rangeDays, setRangeDays] = useState(RANGE_OPTIONS[0].value)
  const effectiveDays = windowDays ?? rangeDays

  const apps = useMemo(() => getAreaDayApps(areaDays), [areaDays])
  const isStacked = apps.length > 1

  const chartData = useMemo<ChartPoint[]>(() => {
    const totals = padDailyTotals(
      deriveDailyTotals(areaDays),
      effectiveDays,
      windowEnd,
    )

    const areasByDay = new Map<string, Map<string, number>>()
    const appsByDay = new Map<string, Map<string, number>>()
    for (const areaDay of areaDays) {
      const day = String(areaDay.Date).slice(0, 10)
      const value = areaDay.WeightedCount ?? areaDay.EventCount

      const areas = areasByDay.get(day) ?? new Map<string, number>()
      areas.set(areaDay.AppArea, (areas.get(areaDay.AppArea) ?? 0) + value)
      areasByDay.set(day, areas)

      if (areaDay.AppCode != null) {
        const dayApps = appsByDay.get(day) ?? new Map<string, number>()
        dayApps.set(
          areaDay.AppCode,
          (dayApps.get(areaDay.AppCode) ?? 0) + value,
        )
        appsByDay.set(day, dayApps)
      }
    }

    const points = totals.map((entry) => {
      const point: ChartPoint = {
        day: entry.day,
        value: Math.round(entry.value),
        rawCount: entry.rawCount,
        trend: 0,
        topAreas: [...(areasByDay.get(entry.day) ?? new Map())]
          .map(([area, value]) => ({ area, value }))
          .sort((a, b) => b.value - a.value)
          .slice(0, TOOLTIP_AREA_LIMIT),
      }
      for (const app of apps) {
        point[`app:${app}`] = Math.round(
          appsByDay.get(entry.day)?.get(app) ?? 0,
        )
      }
      return point
    })

    // Trailing mean over the days seen so far (shorter at the window's
    // start), so the line starts at day one instead of appearing mid-chart.
    let rollingSum = 0
    for (const [index, point] of points.entries()) {
      rollingSum += point.value
      if (index >= TREND_WINDOW_DAYS) {
        rollingSum -= points[index - TREND_WINDOW_DAYS].value
      }
      point.trend = Math.round(
        rollingSum / Math.min(index + 1, TREND_WINDOW_DAYS),
      )
    }
    return points
  }, [areaDays, effectiveDays, windowEnd, apps])

  const hasData = chartData.some((point) => point.value > 0)

  // Norwegian vacation spans inside the visible window, shaded on the chart
  // so a July/Easter/Christmas dip reads as "everyone's away", not churn.
  const holidayBands = useMemo(() => {
    if (chartData.length < 2) return []
    const first = new Date(chartData[0].day)
    const last = new Date(chartData[chartData.length - 1].day)
    return getHolidaySpans(first, last).flatMap((span) => {
      const inSpan = chartData.filter((point) => {
        const day = new Date(point.day)
        return day >= span.from && day <= span.to
      })
      // A one-day sliver has no room to read as a band.
      if (inSpan.length < 2) return []
      return [
        {
          key: span.key,
          label: span.label,
          x1: inSpan[0].day,
          x2: inSpan[inSpan.length - 1].day,
        },
      ]
    })
  }, [chartData])

  return (
    <div className={cn("flex flex-col gap-2", className)} {...divProps}>
      {!hideHeader && (
        <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
            <h3 className="text-sm font-semibold">Daily activity</h3>
            <span
              className="text-xs text-muted-foreground"
              title="Weighted = heavy actions (exports, reports, saves) count more than clicks."
            >
              weighted usage actions per day
            </span>
            {isStacked && (
              <span className="flex items-center gap-2 text-xs text-muted-foreground">
                {apps.map((app) => (
                  <span key={app} className="flex items-center gap-1">
                    <span
                      className="size-2 rounded-full"
                      style={{ backgroundColor: getAppCodeColor(app) }}
                    />
                    {formatAppCode(app)}
                  </span>
                ))}
              </span>
            )}
            {hasData && (
              <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
                <span
                  aria-hidden
                  className="inline-block w-4 border-t-2 border-dashed border-foreground/60"
                />
                {TREND_WINDOW_DAYS}-day trend
              </span>
            )}
          </div>
          {windowDays == null && (
            <div className="inline-flex items-center rounded-md border border-border overflow-hidden text-xs">
              {RANGE_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  className={cn(
                    "px-3 py-1 transition-colors",
                    rangeDays === opt.value
                      ? "bg-primary text-primary-foreground"
                      : "text-muted-foreground hover:bg-muted",
                  )}
                  onClick={() => setRangeDays(opt.value)}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {!hasData ? (
        <p className="rounded-md border border-dashed px-3 py-6 text-center text-xs text-muted-foreground">
          No activity in the selected window.
        </p>
      ) : (
        // A fixed height: the chart base carries aspect-video, and as a
        // growing flex item that intrinsic ratio would size it from the
        // width — a wide panel got a 500 px chart.
        <Chart config={CHART_CONFIG} className="h-[200px] w-full shrink-0">
          {/* Composed, not Bar: the trendline rides over the daily bars. */}
          <Chart.ComposedChart
            data={chartData}
            margin={{ left: -18, right: 4 }}
          >
            <Chart.Grid vertical={false} />
            {/* X keys on the ISO day (unique even across a 1-year window
                where "1. Jul" repeats); ticks format on render. */}
            <Chart.Axis.X
              dataKey="day"
              tickFormatter={(value: string) =>
                format(new Date(value), "d. MMM")
              }
              tickLine={false}
              axisLine={false}
              tickMargin={6}
              minTickGap={28}
              className="text-xs text-muted-foreground"
            />
            {holidayBands.map((band) => (
              <Chart.ReferenceArea
                key={band.key}
                x1={band.x1}
                x2={band.x2}
                fill="currentColor"
                className="text-muted-foreground"
                fillOpacity={0.07}
                label={{
                  value: band.label,
                  position: "insideTop",
                  fontSize: 10,
                  className: "fill-muted-foreground",
                }}
              />
            ))}
            <Chart.Axis.Y
              tickLine={false}
              axisLine={false}
              tickMargin={4}
              width={48}
              allowDecimals={false}
              className="text-xs text-muted-foreground"
            />
            <Chart.Tooltip content={<UsageActivityTooltip apps={apps} />} />
            {isStacked ? (
              apps.map((app, index) => (
                <Chart.Bar
                  key={app}
                  dataKey={`app:${app}`}
                  stackId="apps"
                  fill={getAppCodeColor(app)}
                  radius={index === apps.length - 1 ? [2, 2, 0, 0] : 0}
                  maxBarSize={18}
                />
              ))
            ) : (
              <Chart.Bar
                dataKey="value"
                fill="var(--color-value)"
                radius={[2, 2, 0, 0]}
                maxBarSize={18}
              />
            )}
            {/* Neutral + dashed so it reads as a reference over the data
                bars, not as another data series. */}
            <Chart.Line
              dataKey="trend"
              type="monotone"
              stroke="hsl(var(--foreground))"
              strokeOpacity={0.55}
              strokeWidth={1.5}
              strokeDasharray="5 4"
              dot={false}
              activeDot={false}
              isAnimationActive={false}
            />
          </Chart.ComposedChart>
        </Chart>
      )}
    </div>
  )
}

// Custom tooltip: "what happened that day", not just a number — weighted
// total, raw event count, the per-product split when both consoles
// contributed, and the day's leading interest areas.
function UsageActivityTooltip(props: {
  active?: boolean
  payload?: { payload: ChartPoint }[]
  apps?: string[]
}) {
  const { active, payload, apps = [] } = props
  const point = payload?.[0]?.payload
  if (!active || !point) return null

  const appSplit = apps
    .map((app) => ({ app, value: Number(point[`app:${app}`] ?? 0) }))
    .filter((entry) => entry.value > 0)

  return (
    <div className="rounded-md border bg-popover px-2.5 py-1.5 text-xs shadow-md">
      <p className="font-medium">
        {format(new Date(point.day), "EEEE d. MMM")}
      </p>
      {point.value > 0 ? (
        <>
          <p className="text-muted-foreground">
            {formatNumber(point.value)} weighted activity ·{" "}
            {formatNumber(point.rawCount)} events
          </p>
          <p className="text-muted-foreground">
            {TREND_WINDOW_DAYS}-day trend: {formatNumber(point.trend)}
          </p>
          {appSplit.length > 1 && (
            <p className="text-muted-foreground">
              {appSplit
                .map(
                  (entry) =>
                    `${formatAppCode(entry.app)} ${formatNumber(entry.value)}`,
                )
                .join(" · ")}
            </p>
          )}
          {point.topAreas.length > 0 && (
            <p className="text-muted-foreground">
              {point.topAreas
                .map((entry) => formatAppArea(entry.area))
                .join(" · ")}
            </p>
          )}
        </>
      ) : (
        <p className="text-muted-foreground">
          No activity
          {point.trend > 0 && (
            <>
              {" "}
              · {TREND_WINDOW_DAYS}-day trend {formatNumber(point.trend)}
            </>
          )}
        </p>
      )}
    </div>
  )
}

export default UsageActivityChart
